import { useState, useCallback } from 'react';
import { ref, set } from 'firebase/database';
import { database } from '../../../../config/firebase';

/**
 * QuizSectionの割り当て機能を担当するカスタムフック
 * 
 * @param {string} studentId - 生徒ID
 * @param {Object} selectedStudent - 選択された生徒情報
 * @param {Array} contents - 利用可能なコンテンツ一覧
 * @returns {Object} 割り当て機能に関する状態とメソッド
 */
const useQuizAssignment = (studentId, selectedStudent, contents) => {
  // 状態管理
  const [isAssigning, setIsAssigning] = useState(false);
  const [selectedContentId, setSelectedContentId] = useState('');
  const [assignmentError, setAssignmentError] = useState(null);

  // 割り当て開始
  const startAssigning = useCallback(() => {
    setIsAssigning(true);
    setSelectedContentId('');
    setAssignmentError(null);
  }, []);

  // 割り当てキャンセル
  const cancelAssigning = useCallback(() => {
    setIsAssigning(false);
    setSelectedContentId('');
    setAssignmentError(null);
  }, []);

  // コンテンツ選択
  const selectContent = useCallback((contentId) => {
    setSelectedContentId(contentId);
    setAssignmentError(null);
  }, []);

  // 選択されたコンテンツの取得
  const getSelectedContent = useCallback(() => {
    if (!selectedContentId || !contents) return null;
    return contents.find(content => content.id === selectedContentId);
  }, [selectedContentId, contents]);

  // 割り当て実行
  const executeAssignment = useCallback(async () => {
    // バリデーション
    if (!selectedStudent) {
      const error = new Error('クイズを割り当てる生徒を選択してください。');
      setAssignmentError(error.message);
      return { success: false, error: error.message };
    }
    
    if (!selectedContentId) {
      const error = new Error('割り当てるコンテンツを選択してください。');
      setAssignmentError(error.message);
      return { success: false, error: error.message };
    }

    const selectedContent = getSelectedContent();
    if (!selectedContent) {
      const error = new Error('選択されたコンテンツが見つかりません。');
      setAssignmentError(error.message);
      return { success: false, error: error.message };
    }

    try {
      // Firebase に割り当て情報を保存
      const assignRef = ref(database, `studentAssignedContents/${studentId}/${selectedContentId}`);
      await set(assignRef, true);
      
      // 状態をリセット
      setSelectedContentId('');
      setIsAssigning(false);
      setAssignmentError(null);
      
      return { 
        success: true, 
        message: `コンテンツ「${selectedContent.title}」が割り当てられました。` 
      };
    } catch (error) {
      console.error('割り当て失敗:', error);
      const errorMessage = '割り当てに失敗しました。';
      setAssignmentError(errorMessage);
      return { success: false, error: errorMessage };
    }
  }, [selectedStudent, selectedContentId, studentId, getSelectedContent]);

  // 割り当て確認用のメッセージ作成
  const getConfirmationMessage = useCallback(() => {
    const selectedContent = getSelectedContent();
    if (!selectedContent) return '';
    return `コンテンツ「${selectedContent.title}」を割り当てますか？`;
  }, [getSelectedContent]);

  // エラークリア
  const clearAssignmentError = useCallback(() => {
    setAssignmentError(null);
  }, []);

  // 割り当て可能かチェック
  const canAssign = useCallback(() => {
    return !!(selectedStudent && selectedContentId && !isAssigning);
  }, [selectedStudent, selectedContentId, isAssigning]);

  return {
    // 状態
    isAssigning,
    selectedContentId,
    assignmentError,

    // メソッド
    startAssigning,
    cancelAssigning,
    selectContent,
    executeAssignment,
    getSelectedContent,
    getConfirmationMessage,
    clearAssignmentError,
    canAssign
  };
};

export default useQuizAssignment; 